import { ReactNode } from 'react';
// 组件
import { ProTooltip, ProButton, ProImage } from '../../antd';
import { DeleteOutlined } from '@ant-design/icons';
import Images from '../../Images';
import CopyColumn from '../../SuperTable/subpage/copyColumn';
import SkuInfoList from '../../SkuInfoList';
import CopyText from '../../CopyText';
import RouteLink from '../../RouteLink';
// 方法
import { useLayoutContext } from '../../PageLayout/context/LayoutContext';
import usePrevious from '../../Hooks/usePrevious';
import { renderEnumNames } from '../../utils/renderUtils';
// 常量
import skuInfoListConfig from '../../constants/skuInfoListConfig';
import { ColumnType } from 'antd/lib/table';
import { FormInstance } from 'antd/lib/form';
import {
  EditVirtualTableProps,
  EditVirtualTableColumnType,
  EditVirtualTableDispatchType,
  IEditVirtualTablePayload,
} from '../interface';
import { TABLE_COLUMN_EMPTY } from '../../constants/constant';
import { getColumnValue } from '../../utils/dataUtils';

function useVirtualTableColumns(
  props: EditVirtualTableProps,
  dispatchEditVirtualTable: (
    type: EditVirtualTableDispatchType,
    payload: IEditVirtualTablePayload,
  ) => void,
  form?: FormInstance,
) {
  const {
    columns,
    disabled,
    operation,
    hideAdd,
    addIcon,
    mergeRowKey,
    operationTitle,
    operationWidth,
    onAdd,
    onDelete,
    rowRender,
  } = props;
  const { enumData } = useLayoutContext();
  const prevDisabled = usePrevious(disabled);
  const handleAdd = async () => {
    let newRowData = {};
    if (onAdd) {
      const res = await onAdd();
      if (res === false) return;
      newRowData = res;
    }
    dispatchEditVirtualTable('addRow', { addItem: newRowData });
  };
  const handleDelete = async (index: number, record: any) => {
    if (onDelete) {
      const result = await onDelete(index, record);
      if (!result) return;
    }
    dispatchEditVirtualTable('deleteRow', { rowKey: record.key });
  };
  const formatColumns = (
    columnsProps?: EditVirtualTableColumnType[],
    isChildren: boolean = false,
  ): EditVirtualTableColumnType[] => {
    let newColumns: EditVirtualTableColumnType[] = [];
    if (Array.isArray(columnsProps) && columnsProps.length) {
      const baseColumnsProps: EditVirtualTableColumnType = {
        ellipsis: {
          showTitle: false,
        },
      };
      // @ts-ignore
      newColumns = columnsProps.map((column, index) => {
        const {
          editable,
          title,
          dataIndex,
          enumCode,
          renderType,
          children,
          fixed,
          relativePathName,
          required,
          skuInfoListProps,
          routeLinkProps,
          ...restProps
        } = column;
        const baseShouldCellUpdate: EditVirtualTableColumnType = fixed
          ? {}
          : {
              shouldCellUpdate: (record: any, prevRecord: any) => {
                if (record.isMainTable) return true;
                if (disabled !== prevDisabled) return true;
                if (typeof dataIndex === 'string') {
                  return record[dataIndex] !== prevRecord[dataIndex];
                }
                return false;
              },
            };
        // 可编辑单元格
        if (editable) {
          return {
            ...baseColumnsProps,
            ...baseShouldCellUpdate,
            ...column,
            title: required ? (
              <div className="edit-table-column-required">{title as ReactNode}</div>
            ) : (
              title
            ),
            render: (val: any, record: any) => {
              if (record.isMainTable && mergeRowKey) {
                return renderMainRow(record, index, mergeRowKey);
              }
              return val;
            },
            onCell: (record: any, recordIndex?: number) => {
              let initialValue: any;
              if (typeof dataIndex === 'string')
                initialValue = record[dataIndex];
              if (
                restProps.formItemType === 'SelectTable' &&
                Array.isArray(dataIndex)
              ) {
                initialValue = record[dataIndex[0]];
              }
              if (!initialValue && restProps.initialValue !== undefined)
                initialValue = restProps.initialValue;
              return {
                record,
                editable,
                disabled,
                form,
                dataIndex,
                mergeRowKey,
                rowRender,
                columnIndex: index,
                rowIndex: recordIndex,
                formItemProps: {
                  required,
                  enumCode,
                  relativePathName,
                  label: title,
                  name: dataIndex,
                  ...restProps,
                  initialValue,
                },
              };
            },
          };
        }
        if (Array.isArray(dataIndex)) {
          return {
            ...baseColumnsProps,
            ...column,
            render: (_: any, record: any) => {
              if (record.isMainTable && mergeRowKey) {
                return renderMainRow(record, index, mergeRowKey);
              }
              return (
                <>
                  {dataIndex.map(item => {
                    const columnValue = getColumnValue(record, item);
                    if (renderType === 'copy') {
                      return <CopyText text={columnValue} isBlock key={item} align={column.align} />;
                    }
                    return (
                      <ProTooltip
                        key={item}
                        placement="topLeft"
                        title={columnValue}
                        mouseEnterDelay={1}
                      >
                        <div className="ant-table-cell-ellipsis">
                          {columnValue || TABLE_COLUMN_EMPTY}
                        </div>
                      </ProTooltip>
                    )
                  })}
                </>
              );
            },
          };
        }
        // 普通单元格
        if (renderType) {
          switch (renderType) {
            case 'skuInfoList':
            const skuInfoListWidth = Number(column.width || 600);
            return {
              ...baseColumnsProps,
              ...column,
              width: skuInfoListWidth + 10,
              render: (_: string, record: any) => {
                const columnValue = getColumnValue(record, dataIndex);
                return (
                  <SkuInfoList
                    data={columnValue}
                    width={skuInfoListWidth}
                    {...skuInfoListConfig}
                    {...skuInfoListProps}
                  />
                );
              },
            };
            case 'image':
              return {
                className: 'table-image-column',
                ...column,
                render: (_: any, record: any) => {
                  const columnValue = getColumnValue(record, dataIndex);
                  if (record.isMainTable && mergeRowKey) {
                    return renderMainRow(record, index, mergeRowKey);
                  }
                  return <ProImage className="table-column-image" src={columnValue} />;
                },
              };
            case 'images':
              return {
                ...baseColumnsProps,
                ...column,
                render: (_: any, record: any) => {
                  const columnValue = getColumnValue(record, dataIndex);
                  if (record.isMainTable && mergeRowKey) {
                    return renderMainRow(record, index, mergeRowKey);
                  }
                  if (Array.isArray(columnValue)) return <Images images={columnValue} />;
                  return null;
                },
              };
            case 'boolean':
              return {
                ...baseColumnsProps,
                ...column,
                render: (_: any, record: any) => {
                  const columnValue = getColumnValue(record, dataIndex);
                  if (record.isMainTable && mergeRowKey) {
                    return renderMainRow(record, index, mergeRowKey);
                  }
                  return columnValue ? '是' : '否';
                },
              };
            case 'copy':
              return {
                ...baseColumnsProps,
                ...column,
                render: (_: any, record: any) => {
                  const columnValue = getColumnValue(record, dataIndex);
                  if (record.isMainTable && mergeRowKey) {
                    return renderMainRow(record, index, mergeRowKey);
                  }
                  return <CopyColumn text={columnValue} />;
                },
              };
            case 'routeLink':
              return {
                ...baseColumnsProps,
                ...column,
                render: (_: any, record: any, key: number) => {
                  const columnValue = getColumnValue(record, dataIndex);
                  const routeLinkQuery = routeLinkProps?.query;
                  const routeLinkState = routeLinkProps?.state;
                  return (
                    <RouteLink
                      text={columnValue}
                      {...routeLinkProps}
                      query={typeof routeLinkQuery === 'function' ? routeLinkQuery(record) : routeLinkQuery}
                      state={typeof routeLinkState === 'function' ? routeLinkState(record) : routeLinkState}
                    />
                  );
                },
              };
          }
        }
        if (enumCode && enumData) {
          const enumItem = enumData[enumCode];
          if (!enumItem)
            return {
              ...baseColumnsProps,
              ...column,
              render: () => {
                return TABLE_COLUMN_EMPTY;
              },
            };
          return enumItem
            ? {
                ...baseColumnsProps,
                ...column,
                render: (_: any, record: any) => {
                  const columnValue = getColumnValue(record, dataIndex);
                  if (record.isMainTable && mergeRowKey) {
                    return renderMainRow(record, index, mergeRowKey);
                  }
                  return renderEnumNames(columnValue, enumItem.items);
                },
              }
            : { ...baseColumnsProps, ...column };
        }
        return {
          ...baseColumnsProps,
          ...baseShouldCellUpdate,
          ...column,
          children: children ? formatColumns(children, true) : undefined,
          render: (_: any, record: any, key: number) => {
            const columnValue = getColumnValue(record, dataIndex);
            if (record.isMainTable && mergeRowKey) {
              return renderMainRow(record, index, mergeRowKey);
            }
            if (columnValue === undefined || columnValue === '' || columnValue === null) {
              return column.render
                ? column.render(columnValue, record, key)
                : TABLE_COLUMN_EMPTY;
            }
            if (typeof columnValue === 'string') {
              // @ts-ignore
              const columnHTML: ReactNode = column.render ? column.render(columnValue, record, key) : columnValue;
              return (
                <ProTooltip placement="topLeft" title={columnValue}>
                  {columnHTML}
                </ProTooltip>
              );
            }
            return column.render ? column.render(columnValue, record, key) : columnValue;
          },
        };
      });
      if (isChildren) return newColumns;
      // 操作列
      if (operation !== false && !disabled) {
        const operationColumns: ColumnType<any> = operation || {
          fixed: 'right',
          width: operationWidth,
          align: 'center',
          title: hideAdd ? null : (
            operationTitle ? operationTitle :
            <ProButton type="primary" size="small" onClick={handleAdd}>
              {addIcon}
            </ProButton>
          ),
          render: (text: any, record: any, key: number) => {
            if (record.isMainTable) {
              return {
                children: '',
                props: {
                  colSpan: 0,
                  className: 'yb-table-row-main',
                },
              };
            }
            return (
              <>
                <DeleteOutlined
                  style={{ color: '#ff4d4f' }}
                  onClick={() => handleDelete(key, record)}
                  title="删除"
                />
              </>
            );
          },
        };
        // @ts-ignore
        newColumns.push(operationColumns);
      }
    }
    return newColumns;
  };
  const renderMainRow = (
    record: any,
    index: number,
    mergeDataIndex: string,
  ) => {
    if (!columns) return;
    return {
      children: rowRender ? rowRender(record) : record[mergeDataIndex],
      props: {
        colSpan: index === 0 ? columns.length + 1 : 0,
      },
    };
  };
  const getSelecTableInfo = (columnsProps?: EditVirtualTableColumnType[]) => {
    const selecTableInfo: any = {};
    columnsProps?.forEach?.(item => {
      const { dataIndex, formItemType } = item;
      if (
        formItemType === 'SelectTable' &&
        Array.isArray(dataIndex) &&
        dataIndex.length === 2
      ) {
        selecTableInfo[dataIndex[0]] = dataIndex[1];
      }
    });
    return selecTableInfo;
  };
  return {
    selecTableInfo: getSelecTableInfo(columns),
    columns: formatColumns(columns),
  };
}

export default useVirtualTableColumns;
